import { useState } from "react";
import { useNavigate, Navigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Loader2, Lock } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { useLogo } from "@/context/AssetsContext";
import { apiError } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";

export default function AdminLogin() {
  const { admin, login } = useAuth();
  const logo = useLogo();
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  if (admin) return <Navigate to="/admin" replace />;

  const submit = async (e) => {
    e.preventDefault();
    setError(""); setLoading(true);
    try {
      await login(email, password);
      nav("/admin");
    } catch (err) {
      setError(apiError(err.response?.data?.detail) || "Login failed");
    } finally { setLoading(false); }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-[#0A0A0A] px-6">
      <div className="absolute left-1/2 top-1/3 h-72 w-72 -translate-x-1/2 rounded-full bg-brand/20 blur-[120px]" />
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
        className="glass-dark relative w-full max-w-sm rounded-3xl p-8 text-white" data-testid="admin-login">
        <div className="mb-6 flex items-center gap-2.5">
          <img src={logo} alt="SOSConnect AI" className="h-10 w-10 object-contain" />
          <span className="text-lg font-bold tracking-tight">SOSConnect <span className="text-brand">AI</span></span>
        </div>
        <h1 className="text-2xl font-bold tracking-tight">Admin sign in</h1>
        <p className="mb-6 mt-1 text-sm text-white/50">Secure access to the launch dashboard.</p>
        <form onSubmit={submit} className="space-y-3">
          <Input type="email" placeholder="Email" value={email} onChange={(e) => setEmail(e.target.value)} className="h-12 rounded-xl border-white/10 bg-white/5 text-white placeholder:text-white/40" data-testid="admin-email" />
          <Input type="password" placeholder="Password" value={password} onChange={(e) => setPassword(e.target.value)} className="h-12 rounded-xl border-white/10 bg-white/5 text-white placeholder:text-white/40" data-testid="admin-password" />
          {error && <p className="text-sm text-sos" data-testid="admin-error">{error}</p>}
          <Button type="submit" disabled={loading} className="h-12 w-full rounded-xl bg-brand text-base font-semibold hover:bg-brand-dark" data-testid="admin-submit">
            {loading ? <Loader2 className="h-5 w-5 animate-spin" /> : <><Lock className="mr-2 h-4 w-4" /> Sign in</>}
          </Button>
        </form>
      </motion.div>
    </div>
  );
}
